/**
 * KAN-871: личный Telegram-аккаунт менеджера в профиле приложения.
 *
 * Состояние аккаунта: GET /me/personal-telegram/ (ключ ['personalTelegram']),
 * запрос уходит только у менеджера (manager_id) с доступом к маршруту.
 * Подключение по шагам: номер -> код из Telegram -> облачный пароль, если
 * он включён. Код и пароль живут только в состоянии хука, не сохраняются.
 * После подключения и отключения сбрасывается ['currentUser']: переписка
 * узнаёт про личный аккаунт из /auth/me (personal_telegram_account).
 */
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PERSONAL_TELEGRAM_TEXTS as T } from '@/lib/personalTelegramTexts';
import { describeApiError, normalizePhone, validatePhone } from '@/lib/telegramConnect';
import {
  personalTelegramApi,
  type PersonalTelegramAccount,
  type PersonalTelegramState,
} from '@/services/api/personalTelegram';

export const PERSONAL_TELEGRAM_KEY = ['personalTelegram'];
const CURRENT_USER_KEY = ['currentUser'];

/** Шаг подключения: null - форма закрыта. */
export type PersonalConnectStep = 'phone' | 'code' | 'password' | null;

export function usePersonalTelegram() {
  const queryClient = useQueryClient();
  const managerId = useCurrentUser()?.data?.manager_id ?? null;
  const canCall = useCanCallApi('GET', '/api/v1/me/personal-telegram/');
  const available = !!managerId && canCall;

  const query = useQuery<PersonalTelegramState>({
    queryKey: PERSONAL_TELEGRAM_KEY,
    queryFn: personalTelegramApi.get,
    enabled: available,
  });

  const [step, setStep] = useState<PersonalConnectStep>(null);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [loginId, setLoginId] = useState('');
  const [error, setError] = useState('');
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const resetFlow = useCallback(() => {
    setStep(null);
    setPhone('');
    setCode('');
    setPassword('');
    setLoginId('');
    setError('');
  }, []);

  const applyAccount = useCallback(
    (account: PersonalTelegramAccount | null) => {
      queryClient.setQueryData<PersonalTelegramState>(PERSONAL_TELEGRAM_KEY, { account });
      void queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY });
    },
    [queryClient]
  );

  const start = useMutation({
    mutationFn: (value: string) => personalTelegramApi.loginStart(value),
    onSuccess: (res) => {
      setLoginId(res.login_id);
      setError('');
      setStep('code');
    },
    onError: (err) => setError(describeApiError(err, T.startFailed)),
  });

  const confirm = useMutation({
    mutationFn: (withPassword: boolean) =>
      personalTelegramApi.loginConfirm({
        login_id: loginId,
        code: code.trim(),
        password: withPassword ? password : undefined,
      }),
    onSuccess: (res) => {
      if (res.needs_password) {
        setError('');
        setStep('password');
        return;
      }
      if (res.account) {
        applyAccount(res.account);
        resetFlow();
      }
    },
    onError: (err) => setError(describeApiError(err, T.confirmFailed)),
  });

  const disconnectMutation = useMutation({
    mutationFn: personalTelegramApi.disconnect,
    onSuccess: () => {
      setConfirmingDisconnect(false);
      setError('');
      applyAccount(null);
    },
    onError: (err) => setError(describeApiError(err, T.disconnectFailed)),
  });

  const requestCode = useCallback(() => {
    const value = normalizePhone(phone.trim());
    const invalid = validatePhone(value);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError('');
    start.mutate(value);
  }, [phone, start]);

  const submitCode = useCallback(() => {
    if (!code.trim()) {
      setError(T.codeRequired);
      return;
    }
    setError('');
    confirm.mutate(false);
  }, [code, confirm]);

  const submitPassword = useCallback(() => {
    if (!password) {
      setError(T.passwordRequired);
      return;
    }
    setError('');
    confirm.mutate(true);
  }, [password, confirm]);

  return {
    available,
    isLoading: query.isLoading,
    isError: query.isError,
    account: query.data?.account ?? null,
    step,
    openConnect: () => {
      resetFlow();
      setStep('phone');
    },
    cancelConnect: resetFlow,
    phone,
    setPhone,
    code,
    setCode,
    password,
    setPassword,
    requestCode,
    submitCode,
    submitPassword,
    busy: start.isPending || confirm.isPending || disconnectMutation.isPending,
    error,
    confirmingDisconnect,
    askDisconnect: () => {
      setError('');
      setConfirmingDisconnect(true);
    },
    cancelDisconnect: () => setConfirmingDisconnect(false),
    disconnect: () => disconnectMutation.mutate(),
  };
}

export type PersonalTelegramControl = ReturnType<typeof usePersonalTelegram>;
